import { useEffect, useMemo } from "react";
import { YahooSingleLanders } from "../commons/Constants";
import { YahooMetaData } from "../types/YahooMetaData";
import { extractYahooMetaData } from "./yahooFeed";

/**
 * Hook to inject an inline script tag into the document head when the page is loaded
 * on a YahooSingleLanders domain.
 *
 * @param scriptContent - The inline JavaScript code to inject
 */
export const useYahooSingleLandersScript = (
  xmlResult: Document | null
): void => {
  // Build Select Tier config only when metadata is available
  const yahooSelectTierScript = useMemo(() => {
    if (!xmlResult) return "";

    const metadata: YahooMetaData = extractYahooMetaData(xmlResult);
    if (!metadata) return "";

    const currentLander =
      Object.values(YahooSingleLanders).find(
        (lander) => lander.hostname === window.location.hostname
      ) || YahooSingleLanders.SEARCH_DOMAIN_PARKING;

    const clarityConfig = {
      source_tag: currentLander.sourceTag,
      ysid: metadata.searchId,
      cid: metadata.clientId,
      ig: metadata.impressionGuid,
      select_tier: {
        clarityId: currentLander.projectId,
        rguid: metadata.rguid,
      },
      test_mode: false,
    };
    return `(function(w,d,t,x,m,l,p){w['YahooSelectTier']=m;w[m]=w[m]||function(){(w[m].q=w[m].q||[]).push(arguments)},w[m].l=1*new Date();l=d.createElement(t),p=d.getElementsByTagName(t)[0];l.type="text/javascript";l.async=1;l.defer=1;l.src=x;p.parentNode.insertBefore(l,p)})(window,document,'script','https://s.yimg.com/ds/scripts/dmSelectTier-v1.1.0.js','selectTier');selectTier('init',${JSON.stringify(
      clarityConfig
    )});`;
  }, [xmlResult]);

  useEffect(() => {
    if (!xmlResult) return;
    if (!yahooSelectTierScript || yahooSelectTierScript.trim() === "") {
      return;
    }

    const isYahooSingleLander = Object.values(YahooSingleLanders).some(
      (lander) => lander.hostname === window.location.hostname
    );

    if (!isYahooSingleLander) {
      return;
    }

    // Use a unique ID to ensure only one script exists at a time
    const scriptId = "yahoo-select-tier-script";

    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      document.head.removeChild(existingScript);
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "text/javascript";
    script.textContent = yahooSelectTierScript;

    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove && scriptToRemove.parentNode) {
        document.head.removeChild(scriptToRemove);
      }
    };
  }, [yahooSelectTierScript, xmlResult]);
};
